// Bookmark collections + bookmark items DTOs (plan.md V.2).
//
// Two entities:
//   - BookmarkCollection — a named folder owned by a user; private or
//     public. Each user has zero or more `kind='user'` collections plus
//     at most one `kind='read-later'` collection (auto-created on first
//     "save for later" gesture).
//   - Bookmark — the (collection × article) join, carrying the user's
//     personal annotations (a free-form note + a personal-tag array).
//
// Personal tags vs. taxonomy tags: tags here are PRIVATE per-bookmark
// labels (e.g. "for-thesis", "snippet"). They have no relationship to
// the public article-tags taxonomy and never surface outside the
// owner's view.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Closed enums — keep in sync with the CHECK constraints on
// bookmark_collections.kind / .visibility.
// ---------------------------------------------------------------------------
export const BookmarkCollectionKind = z.enum(['user', 'read-later']);
/** V1 ships two visibility modes — private (owner only, default) and
 *  public (accessible at /users/:userId/collections/:slug). Token-based
 *  unlisted sharing is deferred to the upcoming Share System (plan.md
 *  VII); when it lands, a `unlisted` value will be added here without
 *  breaking existing rows. */
export const BookmarkCollectionVisibility = z.enum(['private', 'public']);
// ---------------------------------------------------------------------------
// BookmarkCollection — wire shape returned by /me/collections endpoints
// and /users/:userId/collections/:slug.
// ---------------------------------------------------------------------------
export const BookmarkCollection = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    kind: BookmarkCollectionKind,
    /** Stable per-user slug. For `kind='user'` collections it's
     *  generated from `name` at create time and may be edited via PATCH;
     *  for `kind='read-later'` it's the literal string `'read-later'`. */
    slug: z.string().min(1).max(96),
    /** Display name. For Read Later, the name is also `'Read Later'` by
     *  convention but isn't structurally constrained. */
    name: z.string().min(1).max(120),
    description: z.string().max(2000).nullable(),
    /** Uploaded file backing the cover image. */
    coverFileId: z.string().uuid().nullable(),
    /** Server-generated cover image route, not user-supplied external input. */
    coverImageUrl: z.string().nullable(),
    visibility: BookmarkCollectionVisibility,
    /** Item count. Aggregated on read via count(*) over the
     *  (collection_id) index — no denormalised counter on the row. */
    itemCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Bookmark — wire shape for an item inside a collection. Carries the
// JOIN into the article so the SPA can render a card without a second
// round-trip; the article projection is the SAME light-weight summary
// used by the article-list endpoint.
// ---------------------------------------------------------------------------
/** Light-weight article snapshot embedded in a Bookmark. Mirrors a
 *  subset of `articles.ArticleSummary` — kept narrow on purpose so the
 *  bookmark wire shape stays under control. The SPA can hydrate the
 *  full article via `GET /articles/:slug` if it needs more. */
export const BookmarkedArticle = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    coverImageUrl: z.string().url().nullable(),
    authorId: z.string().uuid(),
    /** Article status at the moment of read. An `archived` article is
     *  still listable in the bookmark but a UI badge can flag it. */
    status: z.enum(['draft', 'scheduled', 'published', 'unpublished', 'archived']),
    /** True when the viewer can no longer reach the article via the
     *  normal canRead chain (e.g. it went private after they bookmarked
     *  it). Bookmark stays — the gesture is the user's own — but the
     *  card renders as "no longer accessible". */
    viewerCanReadArticle: z.boolean(),
});
export const Bookmark = z.object({
    collectionId: z.string().uuid(),
    articleId: z.string().uuid(),
    /** Optional free-form note — "why I saved this". */
    note: z.string().max(2000).nullable(),
    /** Personal tags. Lowercase, hyphens permitted; each ≤ 64 chars,
     *  ≤ 32 per bookmark. NOT the article taxonomy. */
    tags: z.array(z.string().min(1).max(64)).max(32),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** Embedded article snapshot — see above. */
    article: BookmarkedArticle,
});
// ---------------------------------------------------------------------------
// BookmarkLocation — what `/me/bookmarks/article/:articleId` returns.
// "Where have I bookmarked this article?" — drives the article page's
// bookmark-button state ("In 2 collections + Read Later").
// ---------------------------------------------------------------------------
export const BookmarkLocation = z.object({
    collectionId: z.string().uuid(),
    /** Mirror of the collection's `kind` so the SPA can render the
     *  Read Later location with a distinct chip without a join. */
    collectionKind: BookmarkCollectionKind,
    collectionName: z.string(),
    collectionSlug: z.string(),
    note: z.string().nullable(),
    tags: z.array(z.string()),
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Slug regex — same shape as articles, slightly relaxed length.
// ---------------------------------------------------------------------------
export const SLUG_REGEX = /^[a-z0-9](-?[a-z0-9])*$/;
//# sourceMappingURL=collection.js.map